import React from 'react';
import { Header } from './components/Header';
import { SearchForm } from './components/SearchForm';
import { VideoGrid } from './components/VideoGrid';
import { VideoPlayerModal } from './components/VideoPlayerModal';
import { searchYouTube } from './services/youtube';
import type { Video } from './services/youtube';

function App() {
  const [isDarkMode, setIsDarkMode] = React.useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark' || 
      (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  const [apiKey, setApiKey] = React.useState(() => {
    return localStorage.getItem('youtube_api_key') || '';
  });
  
  const [searchTerm, setSearchTerm] = React.useState('');
  const [isSearching, setIsSearching] = React.useState(false);
  const [videos, setVideos] = React.useState<Video[]>([]);
  const [error, setError] = React.useState('');
  const [selectedVideo, setSelectedVideo] = React.useState<Video | null>(null);
  const [isPlayerOpen, setIsPlayerOpen] = React.useState(false);

  React.useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  React.useEffect(() => {
    if (apiKey) {
      localStorage.setItem('youtube_api_key', apiKey);
    } else {
      localStorage.removeItem('youtube_api_key');
    }
  }, [apiKey]);

  const handleSearch = React.useCallback(async (query: string) => {
    const activeApiKey = apiKey || undefined; // Use custom key if available
    setIsSearching(true);
    setError('');
    setSearchTerm(query);
    
    try {
      const { videos, message } = await searchYouTube(query, activeApiKey);
      setVideos(videos);
      
      if (message) {
        setError(message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setVideos([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const toggleTheme = () => setIsDarkMode(prev => !prev);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 
      dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 transition-colors">
      <Header 
        isDarkMode={isDarkMode} 
        onThemeToggle={toggleTheme}
        apiKey={apiKey}
        onApiKeyChange={setApiKey}
      />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <SearchForm 
          onSearch={handleSearch}
          isSearching={isSearching}
          error={error}
        />

        <VideoGrid 
          videos={videos.map(video => ({
            ...video,
            onSelect: (video: Video) => {
              setSelectedVideo(video);
              setIsPlayerOpen(true);
            }
          }))}
          loading={isSearching}
          searchTerm={searchTerm}
        />

        <VideoPlayerModal
          isOpen={isPlayerOpen}
          onClose={() => setIsPlayerOpen(false)}
          video={selectedVideo}
        />
      </main>
    </div>
  );
}

export default App;
